import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  AddRounded,
  ArrowUpwardRounded,
  AttachFileRounded,
  AutoAwesomeRounded,
  CloseRounded,
} from "@mui/icons-material";

export default function ChatInputBar({
  input = "",
  setInput = () => {},
  handleSend = () => {},
  uploading = false,
  file = null,
  handleFileChange = () => {},
  selectedSkill = null,
  setSelectedSkill = () => {},
  skillMode = false,
  setSkillMode = () => {},
  skills = [],
  onSelectSkill = () => {},
  disabled = false,
  placeholder = "Ask about this matter, the next step, or your documents…",
}) {
  const sendDisabled = disabled || uploading || (!input.trim() && !file);

  return (
    <Box sx={{ width: "100%", px: { xs: 0, sm: 1 }, pb: { xs: 1, sm: 1.5 } }}>
      <Paper
        sx={{
          width: "100%",
          maxWidth: 900,
          mx: "auto",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          bgcolor: "background.paper",
          boxShadow: "0 14px 35px rgba(16,42,67,.10)",
          overflow: "hidden",
        }}
      >
        {(selectedSkill || file) && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, px: 1.5, pt: 1.25 }}>
            {selectedSkill && (
              <Chip
                icon={<AutoAwesomeRounded />}
                label={selectedSkill.label}
                onDelete={() => setSelectedSkill(null)}
                size="small"
                color="secondary"
                variant="outlined"
              />
            )}
            {file && <Chip icon={<AttachFileRounded />} label={file.name} size="small" variant="outlined" />}
          </Box>
        )}

        {!skillMode ? (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.5, p: 1 }}>
            <Tooltip title="Copilot work modes">
              <span>
                <IconButton
                  aria-label="Open copilot work modes"
                  onClick={() => setSkillMode(true)}
                  disabled={disabled}
                  sx={{ mb: 0.25, color: "text.secondary" }}
                >
                  <AddRounded />
                </IconButton>
              </span>
            </Tooltip>

            <input
              id="copilot-file-upload"
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.txt,.md,.rtf,.csv,.xls,.xlsx,.ppt,.pptx"
              onChange={handleFileChange}
              disabled={disabled || uploading}
            />
            <Tooltip title="Attach a matter document (50 MB maximum)">
              <span>
                <IconButton
                  component="label"
                  htmlFor="copilot-file-upload"
                  aria-label="Attach matter document"
                  disabled={disabled || uploading}
                  sx={{ mb: 0.25, color: "text.secondary" }}
                >
                  <AttachFileRounded sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>

            <TextField
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  if (!sendDisabled) handleSend();
                }
              }}
              placeholder={placeholder}
              multiline
              maxRows={6}
              variant="standard"
              disabled={disabled || uploading}
              inputProps={{ "aria-label": "Message Lex Maple Copilot" }}
              InputProps={{
                disableUnderline: true,
                sx: { px: 1, py: 0.8, fontSize: 15, lineHeight: 1.5 },
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />

            <Tooltip title={disabled ? "Configure OpenAI to send messages" : "Send message"}>
              <span>
                <IconButton
                  aria-label="Send message"
                  onClick={handleSend}
                  disabled={sendDisabled}
                  sx={{
                    mb: 0.25,
                    width: 38,
                    height: 38,
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    "&:hover": { bgcolor: "primary.dark" },
                    "&.Mui-disabled": {
                      bgcolor: theme => alpha(theme.palette.text.disabled, 0.12),
                      color: "text.disabled",
                    },
                  }}
                >
                  {uploading ? <CircularProgress size={18} color="inherit" /> : <ArrowUpwardRounded />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Choose a work mode</Typography>
                <Typography variant="caption" color="text.secondary">The mode changes how the copilot structures the response.</Typography>
              </Box>
              <IconButton aria-label="Close work modes" size="small" onClick={() => setSkillMode(false)}><CloseRounded fontSize="small" /></IconButton>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {skills.map(skill => (
                <Chip
                  key={skill.skill}
                  icon={skill.icon}
                  label={skill.label}
                  clickable
                  onClick={() => { onSelectSkill(skill); setSkillMode(false); }}
                  variant="outlined"
                  sx={{ "&:hover": { bgcolor: "action.hover" } }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>
      <Typography align="center" variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
        Verify citations, deadlines, and strategic decisions. Do not upload material you are not authorized to process.
      </Typography>
    </Box>
  );
}
